import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { gql, useQuery, useMutation } from '@apollo/client';
import {
    Container, Header, Segment, Button, Input, Message, Loader, Icon, Dropdown, Checkbox, Divider, Label,
} from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { GET_TAGS } from '../../../queries/tags';
import { useIsAdminLoading } from '../../../hooks/isAdmin';
import { PATHS } from '../../../paths';

/**
 * Setting a manager and an absence limit across many classes at once.
 *
 * Both values are per-class and both are set one class at a time on /classes/:id — which is the
 * right place to change one, and a miserable place to set 340. An organisation adopting class
 * managers is not assigning them one by one; it is saying "everyone in this block, these
 * communities, reports to her".
 *
 * **The preview is the point, not a courtesy.** A tag filter is easy to get wrong in the
 * direction that matters — one tag too few selects the whole organisation — and a bulk write
 * has no undo. So the count comes from the server, using *the same arguments the Apply button
 * will send*, and it counts classes that would actually come out different rather than classes
 * that matched. The button then says the number out loud, which is the confirmation.
 */

const PREVIEW = gql`
  query CourseDefaultsPreview(
    $filters: String
    $includeArchived: Boolean
    $applyManager: Boolean
    $managerUserId: Int
    $applyAbsenceLimit: Boolean
    $absenceLimit: Int
  ) {
    courseDefaultsPreview(
      filters: $filters
      includeArchived: $includeArchived
      applyManager: $applyManager
      managerUserId: $managerUserId
      applyAbsenceLimit: $applyAbsenceLimit
      absenceLimit: $absenceLimit
    ) {
      matchedCount
      changedCount
      sample {
        id
        name
        SKU
        absenceLimit
        manager { id name }
      }
    }
  }
`;

const APPLY = gql`
  mutation ApplyCourseDefaults(
    $filters: String
    $includeArchived: Boolean
    $applyManager: Boolean
    $managerUserId: Int
    $applyAbsenceLimit: Boolean
    $absenceLimit: Int
  ) {
    applyCourseDefaults(
      filters: $filters
      includeArchived: $includeArchived
      applyManager: $applyManager
      managerUserId: $managerUserId
      applyAbsenceLimit: $applyAbsenceLimit
      absenceLimit: $absenceLimit
    ) {
      matchedCount
      changedCount
    }
  }
`;

const GET_MANAGER_CANDIDATES = gql`
  query CourseManagerCandidates {
    courseManagerCandidates { id name }
    courseManagerCandidatesWithoutEmail
  }
`;

/** The dropdown entry that means "take the manager off these classes". */
const NOBODY = '__nobody__';

const ClassDefaultsView = () => {
    // The *loading* variant, not the bare `useIsAdmin`: that one answers `false` while the `me`
    // query is still in flight, and a redirect fired on that answer bounces an admin straight
    // back to Settings before the page has had a chance to know who they are.
    const [isAdmin, adminLoading] = useIsAdminLoading();

    const [tagIds, setTagIds] = useState<string[]>([]);
    const [includeArchived, setIncludeArchived] = useState(false);

    const [applyManager, setApplyManager] = useState(false);
    const [managerValue, setManagerValue] = useState<number | typeof NOBODY | ''>('');

    const [applyAbsenceLimit, setApplyAbsenceLimit] = useState(false);
    const [absenceLimitInput, setAbsenceLimitInput] = useState('');

    const [applied, setApplied] = useState<{ changedCount: number } | null>(null);

    const { data: tagsData, loading: tagsLoading } = useQuery(GET_TAGS, {
        variables: { showPeopleTags: false, showCourseTags: true, excludeRoleTags: true },
    });
    const { data: managerData } = useQuery(GET_MANAGER_CANDIDATES);

    const trimmedLimit = absenceLimitInput.trim();
    const absenceLimit = trimmedLimit === '' ? null : Number(trimmedLimit);
    const absenceLimitIsValid = absenceLimit == null || (!isNaN(absenceLimit) && absenceLimit >= 1);

    const managerUserId = managerValue === '' || managerValue === NOBODY ? null : Number(managerValue);

    // A manager chosen but not yet confirmed as "set it" would preview a write that is not going
    // to happen, so the two travel together.
    const variables = {
        filters: tagIds.join(','),
        includeArchived,
        applyManager,
        managerUserId: applyManager ? managerUserId : null,
        applyAbsenceLimit,
        absenceLimit: applyAbsenceLimit ? absenceLimit : null,
    };

    const nothingChosen = !applyManager && !applyAbsenceLimit;
    // An unfinished choice must not be previewed as "clear it", which is what an empty dropdown
    // would otherwise mean.
    const managerUnfinished = applyManager && managerValue === '';
    const previewable = !managerUnfinished && absenceLimitIsValid;

    const { data, loading, error, refetch } = useQuery(PREVIEW, {
        variables,
        skip: !isAdmin || !previewable,
        fetchPolicy: 'network-only',
    });

    const [applyDefaults, { loading: applying, error: applyError }] = useMutation(APPLY);

    if (adminLoading) {
        return (
            <ResponsiveContainer>
                <Loader active inline="centered" style={{ marginTop: '5em' }}>Loading…</Loader>
            </ResponsiveContainer>
        );
    }

    // Server-gated too — every query and mutation behind this page checks the same thing. This
    // only avoids showing a page whose every button would be refused.
    if (!isAdmin) {
        return <Navigate to={PATHS.ADMIN_SETTINGS} replace />;
    }

    const managerOptions = managerData?.courseManagerCandidates ?? [];
    const managersWithoutEmail: number = managerData?.courseManagerCandidatesWithoutEmail ?? 0;

    const preview = data?.courseDefaultsPreview;
    const changedCount: number = preview?.changedCount ?? 0;
    const matchedCount: number = preview?.matchedCount ?? 0;

    const describeManager = () => {
        if (managerValue === NOBODY) return 'nobody (clearing the manager)';
        const chosen = managerOptions.find((c: any) => c.id === managerUserId);
        return chosen?.name ?? 'the chosen person';
    };

    const confirmation = () => {
        const changes = [
            applyManager ? `manager → ${describeManager()}` : null,
            applyAbsenceLimit ? `absence limit → ${absenceLimit ?? 'the default (2)'}` : null,
        ].filter(Boolean).join('\n');

        return `Change ${changedCount} ${changedCount === 1 ? 'class' : 'classes'}?\n\n${changes}\n\nThis cannot be undone in bulk.`;
    };

    const handleApply = async () => {
        if (!window.confirm(confirmation())) {
            return;
        }

        const result = await applyDefaults({ variables });
        setApplied({ changedCount: result.data?.applyCourseDefaults?.changedCount ?? 0 });
        await refetch();
    };

    return (
        <ResponsiveContainer>
            <Container style={{ paddingTop: '2em', paddingBottom: '4em' }}>
                <Header as="h1">Class defaults</Header>
                <p style={{ color: '#767676', maxWidth: '46em' }}>
                    Set the <strong>manager</strong> and the <strong>absence limit</strong> on every class
                    carrying the tags you choose. Nothing is written until you press Apply, and the count
                    on the button is the number of classes that would actually change.
                </p>

                <Segment>
                    <Header as="h3" style={{ marginBottom: '0.2em' }}>1. Which classes</Header>
                    <p style={{ color: '#767676' }}>
                        A class must carry <em>every</em> tag listed. Choose none and this is every class
                        in the organisation.
                    </p>
                    <Dropdown
                        placeholder="Tags — e.g. a block and a community"
                        fluid
                        multiple
                        search
                        selection
                        loading={tagsLoading}
                        value={tagIds}
                        options={(tagsData?.tags ?? []).map((t: any) => ({
                            key: t.id, text: t.name, value: `tagId:${t.id}`,
                        }))}
                        onChange={(_, { value }) => { setTagIds(value as string[]); setApplied(null); }}
                    />
                    <Checkbox
                        style={{ marginTop: '1em' }}
                        label="Include archived classes"
                        checked={includeArchived}
                        onChange={(_, d) => { setIncludeArchived(!!d.checked); setApplied(null); }}
                    />
                    {tagIds.length === 0 && (
                        <Message warning style={{ marginTop: '1em' }}>
                            <Icon name="exclamation triangle" />
                            No tags selected — this applies to every class in the organisation.
                        </Message>
                    )}
                </Segment>

                <Segment>
                    <Header as="h3" style={{ marginBottom: '0.2em' }}>2. What to set</Header>
                    <p style={{ color: '#767676' }}>
                        Anything left unticked is left exactly as it is on each class.
                    </p>

                    <Checkbox
                        label="Set the class manager"
                        checked={applyManager}
                        onChange={(_, d) => { setApplyManager(!!d.checked); setApplied(null); }}
                    />
                    <div style={{ margin: '0.6em 0 1.5em 1.8em' }}>
                        <Dropdown
                            selection
                            search
                            fluid
                            disabled={!applyManager}
                            placeholder="Choose a manager"
                            value={managerValue}
                            options={[
                                { key: NOBODY, value: NOBODY, text: '— Nobody (clear the manager) —' },
                                ...managerOptions.map((c: any) => ({
                                    key: c.id, value: c.id, text: c.name,
                                })),
                            ]}
                            onChange={(_, { value }) => { setManagerValue(value as any); setApplied(null); }}
                        />
                        <p style={{ color: '#767676', fontSize: '0.9em', marginTop: '0.4em' }}>
                            The manager is emailed at 9am with any class of theirs that ran the day before
                            and has no attendance recorded. Being the manager grants no access to the class.
                        </p>
                        {/* Only people with an email address can be chosen, because receiving
                            that email is the entire role. Said out loud: otherwise a short or
                            empty list reads as a broken page rather than missing profile data. */}
                        {managersWithoutEmail > 0 && (
                            <Message warning size="tiny">
                                <Icon name="mail" />
                                {managerOptions.length === 0
                                    ? `Nobody can be chosen — all ${managersWithoutEmail} staff in this organisation have no email address on file.`
                                    : `${managersWithoutEmail} ${managersWithoutEmail === 1 ? 'person is' : 'people are'} not listed: no email address on file.`}
                                {' '}A manager needs one to be told about missing attendance.
                            </Message>
                        )}
                    </div>

                    <Checkbox
                        label="Set the absence limit"
                        checked={applyAbsenceLimit}
                        onChange={(_, d) => { setApplyAbsenceLimit(!!d.checked); setApplied(null); }}
                    />
                    <div style={{ margin: '0.6em 0 0 1.8em' }}>
                        <Input
                            type="number"
                            min={1}
                            disabled={!applyAbsenceLimit}
                            placeholder="Leave empty for the default (2)"
                            value={absenceLimitInput}
                            onChange={(e: any) => { setAbsenceLimitInput(e.target.value); setApplied(null); }}
                            style={{ maxWidth: '20em' }}
                        />
                        {!absenceLimitIsValid && (
                            <p style={{ color: '#9f3a38', fontSize: '0.9em', marginTop: '0.4em' }}>
                                The absence limit must be a whole number of 1 or more.
                            </p>
                        )}
                    </div>
                </Segment>

                <Segment>
                    <Header as="h3" style={{ marginBottom: '0.2em' }}>3. Preview</Header>

                    {nothingChosen && (
                        <p style={{ color: '#767676' }}>Choose something to set above.</p>
                    )}

                    {!nothingChosen && managerUnfinished && (
                        <p style={{ color: '#767676' }}>Choose a manager above.</p>
                    )}

                    {!nothingChosen && previewable && loading && (
                        <Loader active inline size="tiny">Counting…</Loader>
                    )}

                    {error && <Message error content="Could not work out what this would change." />}

                    {!nothingChosen && previewable && !loading && preview && (
                        <>
                            <Header as="h2" style={{ marginTop: 0 }}>
                                {changedCount}
                                <Header.Subheader>
                                    {changedCount === 1 ? 'class would change' : 'classes would change'}
                                    {', of '}{matchedCount}{' matching the tags'}
                                    {matchedCount > changedCount && ' — the rest already have these values.'}
                                </Header.Subheader>
                            </Header>

                            {(preview.sample ?? []).length > 0 && (() => {
                                // The sample is drawn from the classes that would change, and
                                // only falls back to the matched ones while nothing is being
                                // set yet — so "and N more" has to count against the same set
                                // it is a sample of, not against whichever number is larger.
                                const sampledFrom = changedCount > 0 ? changedCount : matchedCount;

                                return (
                                    <div style={{ marginTop: '1em' }}>
                                        {preview.sample.map((c: any) => (
                                            <Label key={c.id} basic style={{ margin: '0 0.4em 0.4em 0' }}>
                                                {c.name}
                                                <Label.Detail>
                                                    {c.manager?.name ?? 'unassigned'} · limit {c.absenceLimit ?? 2}
                                                </Label.Detail>
                                            </Label>
                                        ))}
                                        {sampledFrom > preview.sample.length && (
                                            <span style={{ color: '#767676' }}>
                                                …and {sampledFrom - preview.sample.length} more
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}
                        </>
                    )}

                    <Divider />

                    {applied && (
                        <Message
                            success
                            content={`Done — ${applied.changedCount} ${applied.changedCount === 1 ? 'class' : 'classes'} changed.`}
                        />
                    )}
                    {applyError && <Message error content={applyError.message} />}

                    <Button
                        primary
                        loading={applying}
                        disabled={applying || nothingChosen || !previewable || loading || changedCount === 0}
                        onClick={handleApply}
                    >
                        <Icon name="check" />
                        {changedCount === 0
                            ? 'Nothing to apply'
                            : `Apply to ${changedCount} ${changedCount === 1 ? 'class' : 'classes'}`}
                    </Button>
                </Segment>
            </Container>
        </ResponsiveContainer>
    );
};

export { ClassDefaultsView };
